import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';
import { normalizeText } from '../utils/normalizeText';

interface Option {
  value: string | number;
  label: string;
  [key: string]: any;
}

interface SearchableSelectProps {
  options: Option[];
  value: string | number | undefined;
  onChange: (value: string | number | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  renderOption?: (option: Option) => React.ReactNode;
  filterBy?: (option: Option, searchTerm: string) => boolean;
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  onCreateNew?: (searchTerm: string) => void;
  createNewLabel?: (searchTerm: string) => string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
  clearable = true,
  renderOption,
  filterBy,
  id,
  ariaLabel,
  ariaLabelledBy,
  onCreateNew,
  createNewLabel
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(option => option.value === value);

  const defaultFilterBy = (option: Option, searchTerm: string) => {
    return normalizeText(option.label).includes(normalizeText(searchTerm));
  };

  const filteredOptions = options.filter(option => 
    (filterBy || defaultFilterBy)(option, searchTerm)
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        // Only select an existing (highlighted) option on Enter. Never create a
        // new record from Enter — that path silently created patients whenever a
        // typed name matched nothing (esp. on mobile, where Enter = return/✓ key).
        // Creating a new record must be an explicit tap on the "+ create" row.
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleCreateNew = () => {
    if (!onCreateNew) return;
    const term = searchTerm.trim();
    if (!term) return;
    onCreateNew(term);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    }
  };

  return (
    <div style={{ position: 'relative', fontFamily: 'var(--font-sans)' }} className={className} ref={dropdownRef}>
      <div
        style={{
          width: '100%',
          padding: '8px 12px',
          border: isOpen ? '2px solid var(--accent)' : '1px solid var(--line)',
          borderRadius: 'var(--radius)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? 'var(--bg-alt)' : 'var(--bg)',
          color: 'var(--ink)',
          outline: 'none',
          boxSizing: 'border-box',
        }}
        id={id}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: selectedOption ? 'var(--ink)' : 'var(--ink-muted)' }}>
            {selectedOption ? selectedOption.label : (placeholder || t('common.selectOption'))}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {clearable && selectedOption && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  padding: '4px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '50%',
                  minHeight: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ink-faint)',
                }}
              >
                <X style={{ width: 12, height: 12 }} />
              </button>
            )}
            <ChevronDown
              style={{
                width: 16,
                height: 16,
                color: 'var(--ink-faint)',
                transition: 'transform 0.2s',
                transform: isOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </div>
        </div>
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          zIndex: 50,
          width: '100%',
          marginTop: '4px',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '240px',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid var(--line-soft)' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder || t('common.search')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(-1);
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '0.875rem',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-sm)',
                outline: 'none',
                background: 'var(--bg)',
                color: 'var(--ink)',
                boxSizing: 'border-box',
                fontFamily: 'var(--font-sans)',
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div style={{ maxHeight: '192px', overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              onCreateNew && searchTerm.trim() ? (
                <div
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={handleCreateNew}
                >
                  + {createNewLabel ? createNewLabel(searchTerm.trim()) : `Create "${searchTerm.trim()}"`}
                </div>
              ) : (
                <div style={{ padding: '8px 12px', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                  No options found
                </div>
              )
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    background: index === highlightedIndex || option.value === value
                      ? 'var(--bg-alt)'
                      : 'transparent',
                    color: index === highlightedIndex || option.value === value
                      ? 'var(--ink)'
                      : 'var(--ink)',
                  }}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {renderOption ? renderOption(option) : option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;

